/**
 * Abuse-signal watcher.
 *
 * Watches for the signals a browser can actually observe and reports them
 * through `onEvent(type, detail)`. It never blocks anything by itself except
 * by calling preventDefault() on the keys/gestures listed below; what to DO
 * about a signal (log, pause, show a message) is up to the subscriber.
 *
 * Events emitted:
 *   devtools_open, devtools_close      DevTools appeared / went away (heuristic)
 *   devtools_shortcut                  F12, Ctrl+Shift+I/J/C, Cmd+Opt+I/J/C
 *   screenshot_attempt                 PrintScreen, Meta+Shift+3/4/5
 *   copy_attempt                       copy or cut inside the container
 *   context_menu                       right-click inside the container
 *   tab_hidden, tab_visible            page visibility changes
 *   tab_hidden_threshold               tab stayed hidden past tabSwitchThresholdMs while playing
 *   page_closing                       beforeunload / pagehide (fires once)
 *
 * Every API is feature-checked and every handler is wrapped, so a missing
 * browser feature or a throwing subscriber never breaks playback.
 */

const noop = () => {};
const THROTTLED = new Set(["devtools_shortcut", "screenshot_attempt", "copy_attempt", "context_menu"]);
const DEVTOOLS_KEYS = new Set(["KeyI", "KeyJ", "KeyC"]);
const SCREENSHOT_DIGITS = new Set(["Digit3", "Digit4", "Digit5"]);

const num = (v, fallback, min, max) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};
const now = () => (typeof performance !== "undefined" && performance.now ? performance.now() : Date.now());

export class Security {
  /**
   * @param {HTMLElement} container  the player container
   * @param {Object} options
   * @param {(type:string, detail:Object)=>void} options.onEvent
   * @param {"log"|"pause"} [options.devtools="log"]
   * @param {"log"|"pause"} [options.tabSwitch="log"]
   * @param {number} [options.tabSwitchThresholdMs=20000]
   * @param {boolean} [options.debuggerTrap=true]      run the `debugger` timing check
   * @param {"page"|"container"} [options.shortcutScope="page"]
   * @param {number} [options.devtoolsPollMs=1000]
   */
  constructor(container, options = {}) {
    const o = options && typeof options === "object" ? options : {};
    this.container = container || null;
    this.options = {
      devtools: o.devtools === "pause" ? "pause" : "log",
      tabSwitch: o.tabSwitch === "pause" ? "pause" : "log",
      tabSwitchThresholdMs: num(o.tabSwitchThresholdMs, 20000, 0, 3600000),
      debuggerTrap: o.debuggerTrap !== false,
      shortcutScope: o.shortcutScope === "container" ? "container" : "page",
      devtoolsPollMs: num(o.devtoolsPollMs, 1000, 200, 10000),
    };
    this._onEvent = typeof o.onEvent === "function" ? o.onEvent : noop;

    this._attached = false;
    this._cleanups = [];
    this._lastEmit = Object.create(null);
    this._playing = false;

    // visibility
    this._hiddenAt = null;
    this._hiddenWhilePlaying = false;
    this._hiddenTimer = null;
    this._totalHiddenMs = 0;

    // devtools
    this._open = false;
    this._flips = 0;
    this._n = 0;
    this._lastSample = 0;
    this._dbgOpen = false;
    this._noFn = false;
    this._dpr0 = 0;
    this._pollTimer = null;
    this._resizeTimer = null;

    this._closing = false;
  }

  /* ------------------------------------------------------------------ API */

  attach() {
    if (this._attached) return;
    if (typeof window === "undefined" || typeof document === "undefined") return;
    this._attached = true;
    this._dpr0 = window.devicePixelRatio || 1;

    this._listen(document, "keydown", (e) => this._onKey(e, "keydown"), true);
    this._listen(document, "keyup", (e) => this._onKey(e, "keyup"), true);
    this._listen(document, "visibilitychange", () => this._onVisibility());
    this._listen(window, "beforeunload", () => this._emitClosing());
    this._listen(window, "pagehide", () => this._emitClosing());
    this._listen(window, "resize", () => this._onResize());

    if (this.container) {
      this._listen(this.container, "copy", (e) => this._onClipboard(e, "copy"), true);
      this._listen(this.container, "cut", (e) => this._onClipboard(e, "cut"), true);
      this._listen(this.container, "contextmenu", (e) => this._onContextMenu(e), true);
    }

    this._pollTimer = setInterval(() => this._sample(), this.options.devtoolsPollMs);
  }

  detach() {
    this._attached = false;
    for (const off of this._cleanups) {
      try {
        off();
      } catch {
        /* ignore */
      }
    }
    this._cleanups = [];
    clearInterval(this._pollTimer);
    clearTimeout(this._resizeTimer);
    clearTimeout(this._hiddenTimer);
    this._pollTimer = this._resizeTimer = this._hiddenTimer = null;
    this._hiddenAt = null;
  }

  /** Tell the watcher whether the video is currently playing. */
  setPlaying(isPlaying) {
    this._playing = !!isPlaying;
  }

  getStats() {
    let hidden = this._totalHiddenMs;
    if (this._hiddenAt !== null && this._hiddenWhilePlaying) hidden += now() - this._hiddenAt;
    return { totalHiddenMs: Math.round(hidden), devtoolsOpen: this._open };
  }

  /* ------------------------------------------------------------- plumbing */

  _listen(target, type, handler, capture) {
    if (!target || typeof target.addEventListener !== "function") return;
    const safe = (e) => {
      try {
        handler(e);
      } catch {
        /* never let a handler break the page */
      }
    };
    target.addEventListener(type, safe, !!capture);
    this._cleanups.push(() => target.removeEventListener(type, safe, !!capture));
  }

  _emit(type, detail = {}) {
    const t = now();
    const minGap = THROTTLED.has(type) ? 300 : type === "devtools_open" || type === "devtools_close" ? 1000 : 0;
    if (minGap && this._lastEmit[type] && t - this._lastEmit[type] < minGap) return;
    this._lastEmit[type] = t;
    try {
      this._onEvent(type, { ...detail, at: Date.now() });
    } catch {
      /* a throwing subscriber must not break us */
    }
  }

  /* ------------------------------------------------------------- keyboard */

  _inScope(e) {
    if (this.options.shortcutScope === "page") return true;
    const c = this.container;
    if (!c) return false;
    const fs = document.fullscreenElement || document.webkitFullscreenElement;
    return c.contains(e.target) || fs === c;
  }

  _classify(e) {
    const code = e.code || "";
    const key = e.key || "";
    if (key === "F12" || code === "F12") return "devtools_shortcut";
    if (e.ctrlKey && e.shiftKey && !e.altKey && !e.metaKey && DEVTOOLS_KEYS.has(code)) return "devtools_shortcut";
    if (e.metaKey && e.altKey && !e.ctrlKey && DEVTOOLS_KEYS.has(code)) return "devtools_shortcut";
    if (key === "PrintScreen" || code === "PrintScreen") return "screenshot_attempt";
    if (e.metaKey && e.shiftKey && !e.ctrlKey && !e.altKey && SCREENSHOT_DIGITS.has(code)) return "screenshot_attempt";
    return null;
  }

  _onKey(e, phase) {
    if (!e || e.repeat) return;
    const type = this._classify(e);
    if (!type) return;
    if (!this._inScope(e)) return;
    // PrintScreen on Windows only produces a keyup, everything else a keydown.
    const isPrint = type === "screenshot_attempt" && (e.key === "PrintScreen" || e.code === "PrintScreen");
    if (phase === "keyup" && !isPrint) return;
    try {
      e.preventDefault();
    } catch {
      /* some keys cannot be cancelled */
    }
    this._emit(type, { key: e.key, code: e.code, ctrl: !!e.ctrlKey, shift: !!e.shiftKey, alt: !!e.altKey, meta: !!e.metaKey });
  }

  /* ------------------------------------------------------ copy / context */

  _onClipboard(e, kind) {
    try {
      e.preventDefault();
    } catch {
      /* ignore */
    }
    this._emit("copy_attempt", { kind });
  }

  _onContextMenu(e) {
    try {
      e.preventDefault();
    } catch {
      /* ignore */
    }
    this._emit("context_menu", {});
  }

  /* ----------------------------------------------------------- visibility */

  _onVisibility() {
    const hidden = !!document.hidden || document.visibilityState === "hidden";
    if (hidden && this._hiddenAt === null) {
      this._hiddenAt = now();
      this._hiddenWhilePlaying = this._playing;
      this._emit("tab_hidden", { playing: this._playing });
      if (this._hiddenWhilePlaying) {
        clearTimeout(this._hiddenTimer);
        this._hiddenTimer = setTimeout(() => {
          if (this._hiddenAt !== null) {
            this._emit("tab_hidden_threshold", {
              hiddenMs: Math.round(now() - this._hiddenAt),
              thresholdMs: this.options.tabSwitchThresholdMs,
            });
          }
        }, this.options.tabSwitchThresholdMs);
      }
    } else if (!hidden && this._hiddenAt !== null) {
      const elapsed = now() - this._hiddenAt;
      clearTimeout(this._hiddenTimer);
      this._hiddenTimer = null;
      if (this._hiddenWhilePlaying) this._totalHiddenMs += elapsed;
      this._hiddenAt = null;
      this._emit("tab_visible", { hiddenMs: Math.round(elapsed), totalHiddenMs: Math.round(this._totalHiddenMs) });
    }
  }

  /* -------------------------------------------------------------- unload */

  _emitClosing() {
    if (this._closing) return;
    this._closing = true;
    this._emit("page_closing", {});
  }

  /* -------------------------------------------------------------- devtools */

  _onResize() {
    clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => this._sample(), 300); // debounce resize storms
  }

  /** Docked DevTools shrink the inner window but not the outer one. */
  _checkSize() {
    try {
      const w = window;
      if (!w.outerWidth || !w.innerWidth || !w.outerHeight || !w.innerHeight) return false;
      if (document.fullscreenElement || document.webkitFullscreenElement) return false;
      if (typeof w.matchMedia === "function" && w.matchMedia("(pointer: coarse)").matches) return false; // phones/tablets
      const dpr = w.devicePixelRatio || 1;
      if (this._dpr0 && Math.abs(dpr - this._dpr0) > 0.01) return false; // page zoom changed: delta is meaningless
      if (w.outerWidth / w.innerWidth > 1.3 && w.outerHeight / w.innerHeight > 1.3) return false; // zoomed page
      return w.outerWidth - w.innerWidth > 160 || w.outerHeight - w.innerHeight > 200;
    } catch {
      return false;
    }
  }

  _trap() {
    if (!this._noFn) {
      try {
        new Function("debugger")(); // survives minifiers that strip debugger statements
        return;
      } catch {
        this._noFn = true; // CSP forbids eval-style code; use the plain statement from now on
      }
    }
    // eslint-disable-next-line no-debugger
    debugger;
  }

  /** With DevTools open, `debugger` halts execution and the clock jumps. */
  _checkDebugger() {
    try {
      if (!this.options.debuggerTrap || document.hidden) return this._dbgOpen;
      const t0 = now();
      this._trap();
      return now() - t0 > 100;
    } catch {
      return false;
    }
  }

  _sample() {
    if (!this._attached) return;
    const t = now();
    if (t - this._lastSample < 250) return; // coalesce interval + resize triggers
    this._lastSample = t;

    this._n++;
    if (this._n % 3 === 1) this._dbgOpen = this._checkDebugger(); // roughly every 3 s

    const size = this._checkSize();
    const candidate = size || this._dbgOpen;
    if (candidate === this._open) {
      this._flips = 0;
      return;
    }
    // Hysteresis: the new state must be seen twice in a row before we emit.
    this._flips++;
    if (this._flips >= 2) {
      this._flips = 0;
      this._open = candidate;
      this._emit(candidate ? "devtools_open" : "devtools_close", { bySize: size, byDebugger: this._dbgOpen });
    }
  }
}